import type { CurrentUserNetworkFields } from '@ms/yammer-graphql';
import { NetworkType } from '@ms/yammer-graphql';

import type { CurrentUserNetworkList, EntityStatus, YammerState } from '../../state/types';
import { getStatus } from '../../state/types';
import { getCurrentNetwork } from '../networks/selectors';

type GetCurrentUserNetworks = (state: YammerState) => CurrentUserNetworkList | undefined;
/**
 * @deprecated PERFORMANCE This returns a list of entity objects and forces unnecessary re-renders for
 * this consumer's entire component tree. Consumers should select a list of ids, then pass each id
 * to a child component to select the specific network fields they require.
 */
export const getCurrentUserNetworks: GetCurrentUserNetworks = (state: YammerState) =>
  state.currentUserNetworks.fulfilled;

type GetCurrentUserNetworkById = (state: YammerState, networkId: string) => CurrentUserNetworkFields | undefined | null;
/**
 * @deprecated PERFORMANCE This returns an entity object and forces unnecessary re-renders.
 * This should be replaced by a `getCurrentUserNetworkByIdField` selector.
 */
export const getCurrentUserNetworkById: GetCurrentUserNetworkById = (state, networkId) => {
  if (!networkId) {
    return;
  }

  const currentUserNetworks = getCurrentUserNetworks(state);

  return currentUserNetworks
    ? Object.values(currentUserNetworks).filter(({ node: { id } }: CurrentUserNetworkFields) => id === networkId)[0]
    : undefined;
};

type GetCurrentUserNetworksStatus = (state: YammerState) => EntityStatus;
export const getCurrentUserNetworksStatus: GetCurrentUserNetworksStatus = (state) =>
  getStatus(state.currentUserNetworks);

type GetCurrentUserNetwork = (state: YammerState) => CurrentUserNetworkFields | undefined | null;
/**
 * @deprecated PERFORMANCE This returns an entity object and forces unnecessary re-renders.
 * This should be replaced by a `getCurrentUserNetworkField` selector.
 */
export const getCurrentUserNetwork: GetCurrentUserNetwork = (state) => {
  const { id: currentNetworkId } = getCurrentNetwork(state);

  return getCurrentUserNetworkById(state, currentNetworkId);
};

type GetOtherNetworksUnreadCount = (state: YammerState) => number;
export const getOtherNetworksUnreadCount: GetOtherNetworksUnreadCount = (state: YammerState) => {
  const currentUserNetworks = state.currentUserNetworks.fulfilled;

  return currentUserNetworks && currentUserNetworks.length > 0
    ? currentUserNetworks
        .filter((network) => !network?.isCurrent)
        .map((network) => (network ? network.viewerUnreadContentCount : 0))
        .reduce((sum, current) => sum + current, 0)
    : 0;
};

type IsCurrentNetworkPrimary = (state: YammerState) => boolean;
export const isCurrentNetworkPrimary: IsCurrentNetworkPrimary = (state) => {
  const currentUserNetwork = getCurrentUserNetwork(state);

  return !!currentUserNetwork?.isPrimary;
};

type IsCurrentNetworkExternal = (state: YammerState) => boolean;
export const isCurrentNetworkExternal: IsCurrentNetworkExternal = (state) => {
  const currentUserNetwork = getCurrentUserNetwork(state);

  return currentUserNetwork?.networkType === NetworkType.EXTERNAL;
};
